import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { semanticColors } from '@/assets/styles/colors';
import { absenceApprovalService } from '@/api/domains/absence/absenceApproval.service';
import type { BannerPart } from '@/components/ui/banners';
import { useHoldingCompanies } from '@/api/domains/catalog/holding/hooks/useHoldingCompanies';
import {
  useEmployeeDimensionalFilter,
  type DimensionalFilterConfig,
  type UseEmployeeDimensionalFilterResult,
} from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
} from '@/hooks/ui/usePaginatedList';
import { useUI } from '@/hooks/ui/useUI';
import { useAbsenceApprovalMonthlyCuts } from '@/hooks/absence/useAbsenceApprovalMonthlyCuts';
import { useTranslation } from '@/i18n';
import { formatRut } from '@/utils';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';
import type {
  AbsenceApprovalCounters,
  AbsenceApprovalRow,
  AbsenceApprovalStatus,
  AbsenceCounterKey,
  LC_HourValueOut,
} from '@/api/domains/absence/absenceApproval.types';
import type { IndicatorCardVariant } from '@/components/ui/cards';
import type { TableColumn } from '@/components/ui/tables/Table.types';


const DEFAULT_PAGE_SIZE = 25;

/** Sentinel for the "Todos" option of the main Empresa filter. */
export const ALL_COMPANIES_VALUE = 'all';

/** Decimal hours are shown with four decimals and a comma separator, e.g. 12,5000. */
const DECIMAL_PLACES = 4;

export type AbsenceHourFormat = 'HH:MM:SS' | 'DECIMAL';

const EMPTY_HOUR_VALUE: LC_HourValueOut = { hhmmss: '00:00:00', decimalValue: 0 };

const EMPTY_COUNTERS: AbsenceApprovalCounters = {
  calculated: EMPTY_HOUR_VALUE,
  pending: EMPTY_HOUR_VALUE,
  approved: EMPTY_HOUR_VALUE,
  notApproved: EMPTY_HOUR_VALUE,
};

function joinOrUndefined(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

// El panel avanzado de esta pantalla usa las mismas 6 dimensiones + selección explícita de
// colaboradores que Aprobación HHEE (patrón ya establecido). `location` queda fuera por la misma
// decisión de negocio: la pantalla no filtra por ubicación.
const DIMENSIONAL_CONFIG: DimensionalFilterConfig = {
  company: true,
  area: true,
  position: true,
  costCenter: true,
  contractModality: true,
  securityGroup: true,
  employeeStatus: true,
  employee: true,
};

// Los filtros avanzados se aplican solos: se espera este lapso sin cambios antes de consultar.
const ADVANCED_FILTERS_DEBOUNCE_MS = 500;

const DEFAULT_EMPLOYEE_STATUS: EmployeeFilteredStatus = 'vigente';

// Las claves de columna coinciden 1:1 con la whitelist de AbsenceApprovalOrderByFields.
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'rut',
  employeeName: 'employeeName',
  calculatedHours: 'calculatedHours',
  approvedHours: 'approvedHours',
  status: 'status',
};

const STATUS_COLORS: Record<AbsenceApprovalStatus, string> = {
  Pendiente: semanticColors.status.error,
  Parcial: semanticColors.status.warning,
  Aprobada: semanticColors.status.success,
};

export interface AbsenceCounterDefinition {
  key: AbsenceCounterKey;
  label: string;
  tooltip: string;
  /** Título centrado sobre el texto del tooltip. Opcional: no todos los contadores lo requieren. */
  tooltipTitle?: string;
  value: string;
  variant: IndicatorCardVariant;
  isSelected: boolean;
  /** true sin corte, en recálculo, o con el corte en cero: el valor no representa datos reales. */
  muted: boolean;
}

/**
 * Porción de los filtros que aporta el panel avanzado. Se maneja como una unidad porque se aplica
 * completa cada vez que la tabla se recarga.
 */
interface AbsenceApprovalAdvancedFilters {
  employeeIds: number[];
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  locationIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeFilteredStatus;
}

const EMPTY_ADVANCED_FILTERS: AbsenceApprovalAdvancedFilters = {
  employeeIds: [],
  companyIds: [],
  areaIds: [],
  positionIds: [],
  costCenterIds: [],
  locationIds: [],
  contractModalityIds: [],
  groupIds: [],
  employeeStatus: DEFAULT_EMPLOYEE_STATUS,
};

interface AbsenceApprovalFilters extends AbsenceApprovalAdvancedFilters {
  monthlyCutId: number | null;
  filterCompanyId: number | null;
  counterFilter: AbsenceCounterKey;
}

/**
 * Firma canónica de un snapshot de filtros avanzados — ver useOvertimeApproval, mismo criterio: el
 * orden en que el usuario marcó las opciones no debe disparar una recarga si describe el mismo
 * universo.
 */
function buildAdvancedSignature(filters: AbsenceApprovalAdvancedFilters): string {
  return JSON.stringify([
    [...filters.employeeIds].sort((a, b) => a - b),
    [...filters.companyIds].sort(),
    [...filters.areaIds].sort(),
    [...filters.positionIds].sort(),
    [...filters.costCenterIds].sort(),
    [...filters.locationIds].sort(),
    [...filters.contractModalityIds].sort(),
    [...filters.groupIds].sort(),
    filters.employeeStatus,
  ]);
}

export interface UseAbsenceApprovalResult {
  rows: AbsenceApprovalRow[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<AbsenceApprovalRow>[];
  isLoading: boolean;
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  // Main filters
  companyOptions: { value: string; label: string }[];
  selectedCompanyValue: string;
  onCompanyChange: (value: string) => void;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
  descriptionOptions: { value: string; label: string }[];
  selectedYear: number | null;
  selectedMonth: number | null;
  selectedMonthlyCutId: number | null;
  onYearChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  isLoadingCuts: boolean;
  arePeriodFiltersDisabled: boolean;
  /** Segmentos del mensaje "sin cortes" con el énfasis resuelto por i18n. Null si no aplica. */
  noCutsMessageParts: BannerPart[] | null;
  // Counters + format switch
  counters: AbsenceCounterDefinition[];
  onCounterSelect: (key: AbsenceCounterKey) => void;
  hourFormat: AbsenceHourFormat;
  isDecimalFormat: boolean;
  onHourFormatToggle: (useDecimal: boolean) => void;
  // Advanced (dimensional) filters — se aplican solos, sin botón Buscar
  dimensionalConfig: DimensionalFilterConfig;
  dimensionalFilter: UseEmployeeDimensionalFilterResult;
  isAdvancedFiltersDisabled: boolean;
  isCompanyLocked: boolean;
  // Estado del corte
  isMonthlyCutClosed: boolean;
  isRecalculating: boolean;
  canDownload: boolean;
}

export function useAbsenceApproval(): UseAbsenceApprovalResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { isLoaded, canView } = usePagePermissions();

  const { companies, loading: isLoadingCompanies } = useHoldingCompanies();

  const [selectedCompanyValue, setSelectedCompanyValue] = useState<string>('');
  const [counterValues, setCounterValues] = useState<AbsenceApprovalCounters>(EMPTY_COUNTERS);
  const [isMonthlyCutClosed, setIsMonthlyCutClosed] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [hourFormat, setHourFormat] = useState<AbsenceHourFormat>('HH:MM:SS');

  const hasInitialisedCompany = useRef(false);

  const isCompanyLocked = Boolean(selectedCompanyValue)
    && selectedCompanyValue !== ALL_COMPANIES_VALUE;

  const lockedCompanyIds = useMemo(
    () => (isCompanyLocked ? [selectedCompanyValue] : []),
    [isCompanyLocked, selectedCompanyValue],
  );

  const dimensionalFilter = useEmployeeDimensionalFilter(
    DIMENSIONAL_CONFIG,
    DEFAULT_EMPLOYEE_STATUS,
    { lockedCompanyIds },
  );

  // Preselección inicial: la empresa padre (raíz) del holding del usuario.
  useEffect(() => {
    if (hasInitialisedCompany.current || isLoadingCompanies || companies.length === 0) return;

    const parentCompany = companies.find((company) => company.parentHoldingCompanyId === undefined)
      ?? companies[0];

    hasInitialisedCompany.current = true;
    setSelectedCompanyValue(String(parentCompany.id));
  }, [companies, isLoadingCompanies]);

  const initialFilters = useMemo<AbsenceApprovalFilters>(() => ({
    monthlyCutId: null,
    filterCompanyId: null,
    counterFilter: 'calculated',
    ...EMPTY_ADVANCED_FILTERS,
  }), []);

  // Los contadores viajan en un endpoint propio (POST counters), separado del listado paginado, así
  // el header puede refrescarse sin re-paginar. Pero ambos comparten fuente (dbo.ca_ListarAtrasos,
  // un loop por empresa del universo resuelto) y ninguno pagina internamente: repetir la llamada a
  // counters en cada cambio de página/orden/búsqueda recomputa el corte completo sin necesidad,
  // porque esos tres campos no le importan a los contadores. Se cachea la última respuesta y solo
  // se vuelve a pedir cuando cambia alguno de los filtros que sí la afectan (corte, empresa,
  // dimensionales, employeeStatus) — comparados sin `counterFilter`, que tampoco usa el backend
  // (ver LC_GetAbsenceApprovalCountersIn) y solo resalta un contador en el frontend.
  const lastCountersSignatureRef = useRef<string | null>(null);

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AbsenceApprovalFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AbsenceApprovalRow> | null> => {
      if (params.filters.monthlyCutId === null) return null;

      const sharedFilters = {
        monthlyCutId: params.filters.monthlyCutId,
        filterCompanyId: params.filters.filterCompanyId,
        counterFilter: params.filters.counterFilter,
        employeeIds: joinOrUndefined(params.filters.employeeIds.map(String)),
        companyIds: joinOrUndefined(params.filters.companyIds),
        areaIds: joinOrUndefined(params.filters.areaIds),
        positionIds: joinOrUndefined(params.filters.positionIds),
        costCenterIds: joinOrUndefined(params.filters.costCenterIds),
        locationIds: joinOrUndefined(params.filters.locationIds),
        contractModalityIds: joinOrUndefined(params.filters.contractModalityIds),
        groupIds: joinOrUndefined(params.filters.groupIds),
        employeeStatus: params.filters.employeeStatus,
      };

      const { counterFilter: _counterFilter, ...countersRelevantFilters } = sharedFilters;
      const countersSignature = JSON.stringify(countersRelevantFilters);
      const shouldRefreshCounters = countersSignature !== lastCountersSignatureRef.current;

      const [listResponse, countersResponse] = await Promise.all([
        absenceApprovalService.list(
          {
            ...sharedFilters,
            page: params.page,
            pageSize: params.pageSize,
            searchText: params.searchText,
            orderBy: params.sort?.key,
            orderDirection: params.sort?.direction,
          },
          signal,
        ),
        shouldRefreshCounters
          ? absenceApprovalService.getCounters(sharedFilters, signal)
          : Promise.resolve(null),
      ]);

      if (!listResponse.success || !listResponse.data) {
        if (listResponse.error?.code === 'ABORTED') return null;
        setCounterValues(EMPTY_COUNTERS);
        lastCountersSignatureRef.current = null;
        showApiError(listResponse, translate('absence:approval.errors.loadFailed'));
        return null;
      }

      setIsMonthlyCutClosed(listResponse.data.isMonthlyCutClosed);
      setIsRecalculating(listResponse.data.isRecalculating);

      if (shouldRefreshCounters) {
        if (countersResponse?.success && countersResponse.data) {
          setCounterValues(countersResponse.data);
          lastCountersSignatureRef.current = countersSignature;
        } else if (countersResponse?.error?.code !== 'ABORTED') {
          setCounterValues(EMPTY_COUNTERS);
          lastCountersSignatureRef.current = null;
        }
      }

      return {
        items: listResponse.data.items,
        totalRecords: listResponse.data.totalRecords,
      };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<AbsenceApprovalRow, AbsenceApprovalFilters>({
    fetchFn,
    initialFilters,
    initialSort: { key: 'employeeName', direction: 'ASC' },
    initialPageSize: DEFAULT_PAGE_SIZE,
    orderByMap: ORDER_BY_MAP,
    enabled: isLoaded && canView,
  });

  const advancedFilters = useMemo<AbsenceApprovalAdvancedFilters>(() => ({
    employeeIds: dimensionalFilter.selectedEmployeeIds,
    companyIds: dimensionalFilter.dimensionalFilters.companyIds,
    areaIds: dimensionalFilter.dimensionalFilters.areaIds,
    positionIds: dimensionalFilter.dimensionalFilters.positionIds,
    costCenterIds: dimensionalFilter.dimensionalFilters.costCenterIds,
    locationIds: dimensionalFilter.dimensionalFilters.locationIds,
    contractModalityIds: dimensionalFilter.dimensionalFilters.contractModalityIds,
    groupIds: dimensionalFilter.dimensionalFilters.groupIds,
    employeeStatus: dimensionalFilter.dimensionalFilters.status,
  }), [dimensionalFilter.dimensionalFilters, dimensionalFilter.selectedEmployeeIds]);

  const advancedFiltersRef = useRef(advancedFilters);
  useEffect(() => {
    advancedFiltersRef.current = advancedFilters;
  }, [advancedFilters]);

  const appliedAdvancedSignatureRef = useRef<string>(
    buildAdvancedSignature(EMPTY_ADVANCED_FILTERS),
  );

  const monthlyCutIdRef = useRef<number | null>(null);
  useEffect(() => {
    monthlyCutIdRef.current = filters.monthlyCutId;
  }, [filters.monthlyCutId]);

  const onFiltersPatch = useCallback((patch: Partial<AbsenceApprovalFilters>) => {
    appliedAdvancedSignatureRef.current = buildAdvancedSignature(advancedFiltersRef.current);
    setFilters((prev) => ({ ...prev, ...advancedFiltersRef.current, ...patch }));
  }, [setFilters]);

  const {
    isLoadingCuts,
    hasCuts,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    noCutsMessageParts,
  } = useAbsenceApprovalMonthlyCuts({ selectedCompanyValue, onFiltersPatch });

  // Sin cortes disponibles el panel avanzado queda bloqueado y, si estaba abierto, se contrae.
  const {
    isExpanded: isAdvancedExpanded,
    toggleExpanded: toggleAdvancedExpanded,
  } = dimensionalFilter;

  useEffect(() => {
    if (!hasCuts && isAdvancedExpanded) {
      toggleAdvancedExpanded();
    }
  }, [hasCuts, isAdvancedExpanded, toggleAdvancedExpanded]);

  const onCompanyChange = useCallback((value: string) => {
    setSelectedCompanyValue(value);
  }, []);

  const debouncedAdvancedFilters = useDebounce(advancedFilters, ADVANCED_FILTERS_DEBOUNCE_MS);

  useEffect(() => {
    const signature = buildAdvancedSignature(debouncedAdvancedFilters);
    if (signature === appliedAdvancedSignatureRef.current) return;

    if (monthlyCutIdRef.current === null) return;

    appliedAdvancedSignatureRef.current = signature;
    setFilters((prev) => ({ ...prev, ...debouncedAdvancedFilters }));
  }, [debouncedAdvancedFilters, setFilters]);

  // Al hacer clic en el contador ya activo, se desactiva el filtro (vuelve a 'calculated', que ya
  // representa "mostrar todos los estados") en vez de quedarse fijo en el mismo contador — así su
  // borde de selección vuelve al estado visual por defecto.
  const onCounterSelect = useCallback((key: AbsenceCounterKey) => {
    setFilters((prev) => ({
      ...prev,
      counterFilter: prev.counterFilter === key ? 'calculated' : key,
    }));
  }, [setFilters]);

  const onHourFormatToggle = useCallback((useDecimal: boolean) => {
    setHourFormat(useDecimal ? 'DECIMAL' : 'HH:MM:SS');
  }, []);

  const formatHours = useCallback((value: LC_HourValueOut): string => (
    hourFormat === 'DECIMAL'
      ? value.decimalValue.toFixed(DECIMAL_PLACES).replace('.', ',')
      : value.hhmmss
  ), [hourFormat]);

  const companyOptions = useMemo(() => [
    { value: ALL_COMPANIES_VALUE, label: translate('absence:approval.filters.allCompanies') },
    ...companies.map((company) => ({ value: String(company.id), label: company.name })),
  ], [companies, translate]);

  // Sin corte, en recálculo, o con el corte ya cargado pero en cero: los 4 números no representan
  // datos reales, así que se muestran en gris (colors.grays.neutral99) en vez del color de su
  // variant — incluye el borde de selección de "Horas calculadas" (normalmente azul #00B4FF).
  const areCountersMuted = !hasCuts
    || isRecalculating
    || (!loading && counterValues.calculated.decimalValue === 0);

  const counters = useMemo<AbsenceCounterDefinition[]>(() => [
    {
      key: 'calculated',
      label: translate('absence:approval.counters.calculated.label'),
      tooltip: translate('absence:approval.counters.calculated.tooltip'),
      tooltipTitle: translate('absence:approval.counters.calculated.label'),
      value: formatHours(counterValues.calculated),
      variant: 'important',
      isSelected: filters.counterFilter === 'calculated',
      muted: areCountersMuted,
    },
    {
      key: 'pending',
      label: translate('absence:approval.counters.pending.label'),
      tooltip: translate('absence:approval.counters.pending.tooltip'),
      tooltipTitle: translate('absence:approval.counters.pending.label'),
      value: formatHours(
        filters.counterFilter === 'calculated' || filters.counterFilter === 'pending'
          ? counterValues.pending
          : EMPTY_HOUR_VALUE,
      ),
      variant: 'error',
      isSelected: filters.counterFilter === 'pending',
      muted: areCountersMuted,
    },
    {
      key: 'approved',
      label: translate('absence:approval.counters.approved.label'),
      tooltip: translate('absence:approval.counters.approved.tooltip'),
      tooltipTitle: translate('absence:approval.counters.approved.label'),
      value: formatHours(
        filters.counterFilter === 'calculated' || filters.counterFilter === 'approved'
          ? counterValues.approved
          : EMPTY_HOUR_VALUE,
      ),
      variant: 'success',
      isSelected: filters.counterFilter === 'approved',
      muted: areCountersMuted,
    },
    {
      key: 'notApproved',
      label: translate('absence:approval.counters.notApproved.label'),
      tooltip: translate('absence:approval.counters.notApproved.tooltip'),
      tooltipTitle: translate('absence:approval.counters.notApproved.label'),
      value: formatHours(
        filters.counterFilter === 'calculated' || filters.counterFilter === 'notApproved'
          ? counterValues.notApproved
          : EMPTY_HOUR_VALUE,
      ),
      variant: 'alert',
      isSelected: filters.counterFilter === 'notApproved',
      muted: areCountersMuted,
    },
  ], [translate, formatHours, counterValues, filters.counterFilter, areCountersMuted]);

  const columns: TableColumn<AbsenceApprovalRow>[] = useMemo(() => [
    {
      key: 'rut',
      header: translate('absence:approval.table.rut'),
      sortable: true,
      render: (row: AbsenceApprovalRow) => formatRut(row.rut),
    },
    {
      key: 'employeeName',
      header: translate('absence:approval.table.employee'),
      sortable: true,
    },
    {
      key: 'calculatedHours',
      header: translate('absence:approval.table.calculated'),
      sortable: true,
      render: (row: AbsenceApprovalRow) => formatHours(row.calculatedHours),
    },
    {
      key: 'approvedHours',
      header: translate('absence:approval.table.approved'),
      sortable: true,
      render: (row: AbsenceApprovalRow) => formatHours(row.approvedHours),
    },
    {
      key: 'status',
      header: translate('absence:approval.table.status'),
      sortable: true,
      render: (row: AbsenceApprovalRow) => (
        <span style={{ color: STATUS_COLORS[row.status], fontWeight: 600 }}>
          {row.status}
        </span>
      ),
    },
  ], [translate, formatHours]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    isLoading: loading || isLoadingCuts,
    onSearch,
    onSortChange,
    companyOptions,
    selectedCompanyValue,
    onCompanyChange,
    yearOptions,
    monthOptions,
    descriptionOptions,
    selectedYear,
    selectedMonth,
    selectedMonthlyCutId: filters.monthlyCutId,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    isLoadingCuts,
    arePeriodFiltersDisabled: !hasCuts,
    noCutsMessageParts,
    counters,
    onCounterSelect,
    hourFormat,
    isDecimalFormat: hourFormat === 'DECIMAL',
    onHourFormatToggle,
    dimensionalConfig: DIMENSIONAL_CONFIG,
    dimensionalFilter,
    isAdvancedFiltersDisabled: !hasCuts,
    isCompanyLocked,
    isMonthlyCutClosed,
    isRecalculating,
    canDownload: hasCuts && total > 0,
  };
}

export default useAbsenceApproval;
